import React, { useState, useRef, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import { toast } from 'sonner';
import { 
  Plus, Trash2, Play, Pause, Upload, Music, MoveUp, MoveDown,
  Image, Clock, Sparkles, Volume2, VolumeX, ChevronLeft, ChevronRight
} from 'lucide-react';

// Available transitions
const TRANSITIONS = [
  { id: 'none', name: 'None', description: 'No transition' },
  { id: 'fade', name: 'Fade', description: 'Smooth fade between slides' },
  { id: 'slide-left', name: 'Slide Left', description: 'Slide from right to left' },
  { id: 'slide-right', name: 'Slide Right', description: 'Slide from left to right' },
  { id: 'slide-up', name: 'Slide Up', description: 'Slide from bottom to top' },
  { id: 'slide-down', name: 'Slide Down', description: 'Slide from top to bottom' },
  { id: 'zoom-in', name: 'Zoom In', description: 'Zoom into the next slide' },
  { id: 'zoom-out', name: 'Zoom Out', description: 'Zoom out to next slide' },
];

// Available animations for each slide
const ANIMATIONS = [
  { id: 'none', name: 'None', description: 'Static image' },
  { id: 'pan-left', name: 'Pan Left', description: 'Slowly pan from right to left' },
  { id: 'pan-right', name: 'Pan Right', description: 'Slowly pan from left to right' },
  { id: 'pan-up', name: 'Pan Up', description: 'Slowly pan from bottom to top' },
  { id: 'pan-down', name: 'Pan Down', description: 'Slowly pan from top to bottom' },
  { id: 'zoom-in', name: 'Ken Burns (Zoom In)', description: 'Slow zoom in effect' },
  { id: 'zoom-out', name: 'Ken Burns (Zoom Out)', description: 'Slow zoom out effect' },
  { id: 'stomp', name: 'Stomp', description: 'Quick zoom with bounce' },
  { id: 'fade-in', name: 'Fade In', description: 'Fade in from black' },
  { id: 'fade-out', name: 'Fade Out', description: 'Fade out to black' },
];

// Default slide structure
const createSlide = (imageSrc = null) => ({
  id: `slide-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
  image: imageSrc,
  duration: 3, // seconds
  transition: 'fade',
  animation: 'zoom-in',
  text: '',
  textPosition: 'bottom', // top, center, bottom
  textColor: '#FFFFFF',
  textSize: 32,
  textBgColor: 'rgba(0,0,0,0.5)',
});

export const ReelVideoEditor = ({ onSave, initialSlides = [], initialAudio = null }) => {
  const [slides, setSlides] = useState(initialSlides.length > 0 ? initialSlides : [createSlide()]);
  const [selectedSlideIndex, setSelectedSlideIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentPreviewIndex, setCurrentPreviewIndex] = useState(0);
  const [audioFile, setAudioFile] = useState(initialAudio);
  const [audioVolume, setAudioVolume] = useState(80);
  const [isMuted, setIsMuted] = useState(false);
  
  const fileInputRef = useRef();
  const audioInputRef = useRef();
  const previewIntervalRef = useRef();
  const audioRef = useRef();

  const selectedSlide = slides[selectedSlideIndex];

  // Add new slide
  const addSlide = () => {
    const newSlide = createSlide();
    setSlides([...slides, newSlide]);
    setSelectedSlideIndex(slides.length);
    toast.success('New slide added');
  };

  // Remove slide
  const removeSlide = (index) => {
    if (slides.length === 1) {
      toast.error('Cannot remove the last slide');
      return;
    }
    const newSlides = slides.filter((_, i) => i !== index);
    setSlides(newSlides);
    if (selectedSlideIndex >= newSlides.length) {
      setSelectedSlideIndex(newSlides.length - 1);
    }
    toast.success('Slide removed');
  };

  // Move slide up/down
  const moveSlide = (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= slides.length) return;
    
    const newSlides = [...slides];
    [newSlides[index], newSlides[newIndex]] = [newSlides[newIndex], newSlides[index]];
    setSlides(newSlides);
    setSelectedSlideIndex(newIndex);
  };

  // Update slide property
  const updateSlide = useCallback((index, updates) => {
    setSlides(prev => prev.map((slide, i) => 
      i === index ? { ...slide, ...updates } : slide
    ));
  }, []);

  // Handle image upload for a slide
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        updateSlide(selectedSlideIndex, { image: event.target.result });
        toast.success('Image added to slide');
      };
      reader.readAsDataURL(file);
    }
    e.target.value = '';
  };

  // Handle audio upload
  const handleAudioUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setAudioFile({
          name: file.name,
          src: event.target.result
        });
        toast.success('Audio track added');
      };
      reader.readAsDataURL(file);
    }
    e.target.value = '';
  };

  // Preview playback
  const togglePlayback = () => {
    if (isPlaying) {
      clearInterval(previewIntervalRef.current);
      if (audioRef.current) audioRef.current.pause();
      setIsPlaying(false);
    } else {
      setCurrentPreviewIndex(0);
      setIsPlaying(true);
      if (audioRef.current && !isMuted) {
        audioRef.current.currentTime = 0;
        audioRef.current.volume = audioVolume / 100;
        audioRef.current.play();
      }
      
      let slideIndex = 0;
      const playNext = () => {
        if (slideIndex >= slides.length) {
          setIsPlaying(false);
          setCurrentPreviewIndex(0);
          if (audioRef.current) audioRef.current.pause();
          return;
        }
        setCurrentPreviewIndex(slideIndex);
        const currentDuration = slides[slideIndex].duration * 1000;
        slideIndex++;
        previewIntervalRef.current = setTimeout(playNext, currentDuration);
      };
      playNext();
    }
  };

  // Calculate total duration
  const totalDuration = slides.reduce((sum, slide) => sum + slide.duration, 0);

  // Get animation CSS class
  const getAnimationClass = (animation) => {
    const animationMap = {
      'pan-left': 'animate-pan-left',
      'pan-right': 'animate-pan-right',
      'pan-up': 'animate-pan-up',
      'pan-down': 'animate-pan-down',
      'zoom-in': 'animate-zoom-in',
      'zoom-out': 'animate-zoom-out',
      'stomp': 'animate-stomp',
      'fade-in': 'animate-fade-in',
      'fade-out': 'animate-fade-out',
    };
    return animationMap[animation] || '';
  };

  // Export reel data
  const handleSave = () => {
    const reelData = {
      slides: slides,
      audio: audioFile,
      audioVolume: audioVolume,
      totalDuration: totalDuration
    };
    if (onSave) {
      onSave(reelData);
    }
    return reelData;
  };

  return (
    <div className="flex flex-col lg:flex-row gap-4 h-full">
      {/* Left Panel - Timeline & Slides */}
      <div className="lg:w-80 space-y-4 p-4 border border-border/50 rounded-lg bg-card overflow-y-auto max-h-[700px]">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-lg">Video Slides</h3>
          <Button size="sm" onClick={addSlide} data-testid="add-slide-button">
            <Plus className="h-4 w-4 mr-1" />
            Add Slide
          </Button>
        </div>

        {/* Total Duration */}
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Clock className="h-4 w-4" />
          <span>Total: {totalDuration}s ({slides.length} slides)</span>
        </div>

        {/* Slides List */}
        <div className="space-y-2" data-testid="slides-list">
          {slides.map((slide, index) => (
            <div
              key={slide.id}
              className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                selectedSlideIndex === index 
                  ? 'border-primary bg-primary/5' 
                  : 'border-border/50 hover:border-primary/30'
              }`}
              onClick={() => setSelectedSlideIndex(index)}
              data-testid={`slide-${index}`}
            >
              <div className="flex items-center gap-3">
                <div className="w-16 h-16 bg-muted/20 rounded flex items-center justify-center overflow-hidden">
                  {slide.image ? (
                    <img src={slide.image} alt={`Slide ${index + 1}`} className="w-full h-full object-cover" />
                  ) : (
                    <Image className="h-6 w-6 text-muted-foreground" />
                  )}
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium">Slide {index + 1}</p>
                  <p className="text-xs text-muted-foreground">{slide.duration}s • {slide.animation}</p>
                </div>
                <div className="flex flex-col gap-1">
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-6 w-6"
                    onClick={(e) => { e.stopPropagation(); moveSlide(index, 'up'); }}
                    disabled={index === 0}
                  >
                    <MoveUp className="h-3 w-3" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-6 w-6"
                    onClick={(e) => { e.stopPropagation(); moveSlide(index, 'down'); }}
                    disabled={index === slides.length - 1}
                  >
                    <MoveDown className="h-3 w-3" />
                  </Button>
                </div>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="h-8 w-8 text-destructive"
                  onClick={(e) => { e.stopPropagation(); removeSlide(index); }}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>

        {/* Audio Track */}
        <div className="pt-4 border-t border-border/50">
          <Label className="text-sm font-medium flex items-center gap-2 mb-3">
            <Music className="h-4 w-4" />
            Background Audio
          </Label>
          {audioFile ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2 p-2 bg-muted/20 rounded">
                <Music className="h-4 w-4" />
                <span className="text-sm flex-1 truncate">{audioFile.name}</span>
                <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setIsMuted(!isMuted)}>
                  {isMuted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
                </Button>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="h-6 w-6 text-destructive"
                  onClick={() => setAudioFile(null)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Volume: {audioVolume}%</Label>
                <Slider
                  value={[audioVolume]}
                  onValueChange={([val]) => setAudioVolume(val)}
                  max={100}
                  step={1}
                />
              </div>
              <audio ref={audioRef} src={audioFile.src} />
            </div>
          ) : (
            <Button 
              variant="outline" 
              className="w-full" 
              onClick={() => audioInputRef.current?.click()}
              data-testid="upload-audio-button"
            >
              <Upload className="h-4 w-4 mr-2" />
              Upload Audio
            </Button>
          )}
          <input
            ref={audioInputRef}
            type="file"
            accept="audio/*"
            style={{ display: 'none' }}
            onChange={handleAudioUpload}
          />
        </div>
      </div>

      {/* Center - Preview */}
      <div className="flex-1 flex flex-col items-center justify-center bg-muted/20 rounded-lg p-4">
        {/* Preview Controls */}
        <div className="flex items-center gap-4 mb-4">
          <Button 
            variant={isPlaying ? "destructive" : "default"}
            onClick={togglePlayback}
            data-testid="play-button"
          >
            {isPlaying ? <Pause className="h-4 w-4 mr-2" /> : <Play className="h-4 w-4 mr-2" />}
            {isPlaying ? 'Stop' : 'Preview'}
          </Button>
          <span className="text-sm text-muted-foreground">
            {isPlaying ? `Playing slide ${currentPreviewIndex + 1}` : 'Click to preview'}
          </span>
        </div>

        {/* Preview Area (9:16 aspect ratio for Reels) */}
        <div 
          className="relative bg-black rounded-lg overflow-hidden shadow-xl"
          style={{ width: '270px', height: '480px' }}
        >
          {selectedSlide?.image ? (
            <img 
              src={isPlaying ? slides[currentPreviewIndex]?.image : selectedSlide.image}
              alt="Preview"
              className={`w-full h-full object-cover ${isPlaying ? getAnimationClass(slides[currentPreviewIndex]?.animation) : ''}`}
              style={{ animationDuration: `${(isPlaying ? slides[currentPreviewIndex]?.duration : selectedSlide.duration)}s` }}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <div className="text-center text-white/50">
                <Image className="h-12 w-12 mx-auto mb-2" />
                <p className="text-sm">No image</p>
              </div>
            </div>
          )}
          
          {/* Text Overlay */}
          {selectedSlide?.text && (
            <div 
              className={`absolute left-0 right-0 p-4 ${
                selectedSlide.textPosition === 'top' ? 'top-0' :
                selectedSlide.textPosition === 'center' ? 'top-1/2 -translate-y-1/2' :
                'bottom-0'
              }`}
              style={{ backgroundColor: selectedSlide.textBgColor }}
            >
              <p 
                className="text-center"
                style={{ 
                  color: selectedSlide.textColor,
                  fontSize: `${selectedSlide.textSize / 3}px`
                }}
              >
                {selectedSlide.text}
              </p>
            </div>
          )}

          {/* Slide indicator */}
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
            {slides.map((_, idx) => (
              <div 
                key={idx}
                className={`w-2 h-2 rounded-full ${
                  (isPlaying ? currentPreviewIndex : selectedSlideIndex) === idx 
                    ? 'bg-white' 
                    : 'bg-white/40'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Navigation */}
        <div className="flex items-center gap-4 mt-4">
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setSelectedSlideIndex(Math.max(0, selectedSlideIndex - 1))}
            disabled={selectedSlideIndex === 0}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm">Slide {selectedSlideIndex + 1} of {slides.length}</span>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setSelectedSlideIndex(Math.min(slides.length - 1, selectedSlideIndex + 1))}
            disabled={selectedSlideIndex === slides.length - 1}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Right Panel - Slide Properties */}
      <div className="lg:w-72 space-y-4 p-4 border border-border/50 rounded-lg bg-card overflow-y-auto max-h-[700px]">
        <h3 className="font-semibold text-lg">Slide Settings</h3>

        {/* Upload Image */}
        <div className="space-y-2">
          <Label className="text-sm font-medium">Slide Image</Label>
          <Button 
            variant="outline" 
            className="w-full" 
            onClick={() => fileInputRef.current?.click()}
            data-testid="upload-slide-image"
          >
            <Upload className="h-4 w-4 mr-2" />
            {selectedSlide?.image ? 'Change Image' : 'Upload Image'}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handleImageUpload}
          />
        </div>

        {/* Duration */}
        <div className="space-y-2">
          <Label className="text-sm font-medium">Duration: {selectedSlide?.duration}s</Label>
          <Slider
            value={[selectedSlide?.duration || 3]}
            onValueChange={([val]) => updateSlide(selectedSlideIndex, { duration: val })}
            min={1}
            max={10}
            step={0.5}
            data-testid="duration-slider"
          />
        </div>

        {/* Animation */}
        <div className="space-y-2">
          <Label className="text-sm font-medium flex items-center gap-2">
            <Sparkles className="h-4 w-4" />
            Animation
          </Label>
          <Select 
            value={selectedSlide?.animation || 'none'} 
            onValueChange={(val) => updateSlide(selectedSlideIndex, { animation: val })}
          >
            <SelectTrigger data-testid="animation-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ANIMATIONS.map(anim => (
                <SelectItem key={anim.id} value={anim.id}>
                  <div>
                    <span>{anim.name}</span>
                    <p className="text-xs text-muted-foreground">{anim.description}</p>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Transition to Next */}
        <div className="space-y-2">
          <Label className="text-sm font-medium">Transition to Next</Label>
          <Select 
            value={selectedSlide?.transition || 'fade'} 
            onValueChange={(val) => updateSlide(selectedSlideIndex, { transition: val })}
          >
            <SelectTrigger data-testid="transition-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TRANSITIONS.map(trans => (
                <SelectItem key={trans.id} value={trans.id}>
                  <div>
                    <span>{trans.name}</span>
                    <p className="text-xs text-muted-foreground">{trans.description}</p>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Text Overlay */}
        <div className="space-y-2 pt-4 border-t border-border/50">
          <Label className="text-sm font-medium">Text Overlay</Label>
          <Input
            value={selectedSlide?.text || ''}
            onChange={(e) => updateSlide(selectedSlideIndex, { text: e.target.value })}
            placeholder="Add text to slide..."
            data-testid="slide-text-input"
          />
          
          {selectedSlide?.text && (
            <>
              <div className="grid grid-cols-3 gap-2">
                {['top', 'center', 'bottom'].map(pos => (
                  <Button
                    key={pos}
                    variant={selectedSlide?.textPosition === pos ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => updateSlide(selectedSlideIndex, { textPosition: pos })}
                    className="capitalize"
                  >
                    {pos}
                  </Button>
                ))}
              </div>
              
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Text Color</Label>
                  <Input
                    type="color"
                    value={selectedSlide?.textColor || '#FFFFFF'}
                    onChange={(e) => updateSlide(selectedSlideIndex, { textColor: e.target.value })}
                    className="h-9 w-full"
                  />
                </div>
                <div>
                  <Label className="text-xs">Size</Label>
                  <Input
                    type="number"
                    value={selectedSlide?.textSize || 32}
                    onChange={(e) => updateSlide(selectedSlideIndex, { textSize: parseInt(e.target.value) || 32 })}
                    className="h-9"
                    min={12}
                    max={72}
                  />
                </div>
              </div>
            </>
          )}
        </div>

        {/* Save Button */}
        <div className="pt-4 border-t border-border/50">
          <Button className="w-full" onClick={handleSave} data-testid="save-reel-button">
            Save Reel
          </Button>
        </div>
      </div>

      <style jsx>{`
        @keyframes pan-left {
          from { transform: translateX(10%) scale(1.1); }
          to { transform: translateX(-10%) scale(1.1); }
        }
        @keyframes pan-right {
          from { transform: translateX(-10%) scale(1.1); }
          to { transform: translateX(10%) scale(1.1); }
        }
        @keyframes pan-up {
          from { transform: translateY(10%) scale(1.1); }
          to { transform: translateY(-10%) scale(1.1); }
        }
        @keyframes pan-down {
          from { transform: translateY(-10%) scale(1.1); }
          to { transform: translateY(10%) scale(1.1); }
        }
        @keyframes zoom-in {
          from { transform: scale(1); }
          to { transform: scale(1.2); }
        }
        @keyframes zoom-out {
          from { transform: scale(1.2); }
          to { transform: scale(1); }
        }
        @keyframes stomp {
          0% { transform: scale(1); }
          20% { transform: scale(1.15); }
          40% { transform: scale(1.05); }
          60% { transform: scale(1.1); }
          100% { transform: scale(1.05); }
        }
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes fade-out {
          from { opacity: 1; }
          to { opacity: 0; }
        }
        .animate-pan-left { animation: pan-left linear forwards; }
        .animate-pan-right { animation: pan-right linear forwards; }
        .animate-pan-up { animation: pan-up linear forwards; }
        .animate-pan-down { animation: pan-down linear forwards; }
        .animate-zoom-in { animation: zoom-in linear forwards; }
        .animate-zoom-out { animation: zoom-out linear forwards; }
        .animate-stomp { animation: stomp ease-out forwards; }
        .animate-fade-in { animation: fade-in ease-out forwards; }
        .animate-fade-out { animation: fade-out ease-out forwards; }
      `}</style>
    </div>
  );
};
